import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loading } from "../../components/Loading";
import { MainButton } from "../../components/MainButton";
import { BookingHeader } from "../../components/BookingHeader";
import { ConfigType } from "../../contexts/AppConfigContext";
import { useAppConfig } from "../../hooks/useAppConfig";
import { usePluginspace } from "../../hooks/usePluginspace";
import "./index.css";
import Swal from "sweetalert2";
import api from "../../services/api";
import InputMask from "react-input-mask";
import { telephoneNoMask } from "../../functions/mask";
import { getWeekDay, translateWeekday } from "../../functions/util";
import { z } from "zod";
import { MapPin, Users, Calendar, User, Phone, Mail, DollarSign, Sparkles, Clock, Accessibility } from 'lucide-react';

// Schema de validação com Zod
const bookingSchema = z.object({
  name: z.string()
    .min(1, "Nome é obrigatório")
    .refine((val) => val.split(" ").length >= 2, "Nome completo é obrigatório")
    .refine((val) => /^[a-zA-ZÀ-ÿ\s]+$/.test(val), "Nome deve conter apenas letras"),
  email: z.string()
    .min(1, "E-mail é obrigatório")
    .email("E-mail inválido"),
  people: z.number()
    .min(1, "Deve haver pelo menos 1 pessoa")
    .positive("Número de pessoas deve ser positivo"),
  dateBooking: z.string()
    .min(1, "Data é obrigatória"),
  hourId: z.number()
    .min(1, "Horário é obrigatório"),
  environmentId: z.number()
    .min(1, "Ambiente é obrigatório")
});

type BookingFormData = z.infer<typeof bookingSchema>;

// Importar fonte Poppins
if (typeof document !== 'undefined') {
  const link = document.createElement('link');
  link.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap';
  link.rel = 'stylesheet';
  if (!document.head.querySelector('link[href*="Poppins"]')) {
    document.head.appendChild(link);
  }
}

type ImageType = {
  id: string;
  url: string;
};

type HourType = {
  id: string;
  hour: string;
};

type DayType = {
  able: boolean;
  day:
    | "sunday"
    | "monday"
    | "tuesday"
    | "wednesday"
    | "thursday"
    | "friday"
    | "saturday";
  hours: HourType[];
};

type PriceGroupRange = {
  minPeople: number;
  maxPeople: number;
  price: number;
};

type EnvironmentType = {
  id: string;
  name: string;
  seatsAvailable: number;
  tables: number;
  pricePerSeat?: number | null;
  pricePerEnvironment?: number | null;
  pricePerTable?: number | null;
  priceByGroup?: PriceGroupRange[] | null;
  seatsPerTable?: number | null;
};

type BookingConfigType = {
  id: string;
  able: boolean;
  dayConfirm: boolean;
  expiryTime: number;
  maxPeople: number;
  paymentConfirm: boolean;
  valuePaymentConfirm: number;
  days: DayType[];
  images: ImageType[];
  environment: EnvironmentType[];
};

type BookingType = {
  id: string;
  name: string;
  phone: string;
  people: number;
  email: string;
  date: string;
  bookingHourId: string;
  bookingEnvironmentId: string;
  accessibilityNeeds?: 'Apoio ao Idoso' | 'Cadeirante' | 'Deficiente Físico' | null;
};

export function BookingUpdate() {
  const { pluginspace_id, id } = useParams();
  const { registerAccess, getToken, getThisPluginspace } = usePluginspace();
  const navigate = useNavigate();

  const { getAppConfig } = useAppConfig();
  const [config, setConfig] = useState<ConfigType | void>();
  const [loading, setLoading] = useState(true);
  const [bookingConfig, setBookingConfig] = useState<BookingConfigType | null>(
    null
  );

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [dateBooking, setDateBooking] = useState("");
  const [hours, setHours] = useState([
    { value: 0, select: "Selecione um horário" },
  ]);
  const [hourId, setHourId] = useState(0);
  const [environmentId, setEnvironmentId] = useState(0);
  const [people, setPeople] = useState(0);
  const [phone, setPhone] = useState("");
  const [maskTelephone, setMaskTelephone] = useState("(99) 9999-9999");
  const [accessibilityNeeds, setAccessibilityNeeds] = useState<'Apoio ao Idoso' | 'Cadeirante' | 'Deficiente Físico' | null>(null);

  // Estados para validação
  const [errors, setErrors] = useState<Partial<Record<keyof BookingFormData, string>>>({});
  const [touchedFields, setTouchedFields] = useState<Partial<Record<keyof BookingFormData, boolean>>>({});

  // Estados para cálculo de preço
  const [calculatedPrice, setCalculatedPrice] = useState<number | null>(null);
  const [priceInReais, setPriceInReais] = useState(0);
  const [priceType, setPriceType] = useState<string | null>(null);
  const [priceBreakdown, setPriceBreakdown] = useState<any>(null);
  const [requiresPayment, setRequiresPayment] = useState(false);

  // Função para formatação de moeda
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  useEffect(() => {
    registerAccess("booking/update");
    init();
  }, []);

  // Validar campo específico
  const validateField = (fieldName: keyof BookingFormData, value: any) => {
    try {
      const fieldSchema = bookingSchema.shape[fieldName];
      fieldSchema.parse(value);
      setErrors(prev => ({ ...prev, [fieldName]: undefined }));
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const zodError = error as z.ZodError;
        setErrors(prev => ({ ...prev, [fieldName]: zodError.issues[0].message }));
      }
      return false;
    }
  };

  // Marcar campo como tocado
  const handleBlur = (fieldName: keyof BookingFormData, value: any) => {
    setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
    validateField(fieldName, value);
  };

  // Recalcular preço quando ambiente ou pessoas mudarem
  useEffect(() => {
    console.log('[BookingUpdate] useEffect disparado:', { environmentId, people });
    
    async function calculatePrice() {
      if (!environmentId || people <= 0) {
        console.log('[BookingUpdate] Condições não atendidas:', { environmentId, people });
        setCalculatedPrice(null);
        setPriceType(null);
        setRequiresPayment(false);
        return;
      }

      console.log('[BookingUpdate] Chamando API calculate-price...');
      try {
        const response = await api.post(
          '/api/queueReservation/booking/calculate-price',
          {
            bookingEnvironmentId: environmentId,
            people: people,
          },
          {
            headers: {
              Authorization: `Bearer ${await getToken()}`,
            },
          }
        );

        console.log('[BookingUpdate] Resposta da API:', response.data);

        if (response.data) {
          const { price, priceType: type, breakdown, requiresPayment: reqPayment } = response.data;
          setCalculatedPrice(price);
          setPriceInReais(price / 100);
          setPriceType(type);
          setPriceBreakdown(breakdown);
          setRequiresPayment(reqPayment);
          
          console.log('[BookingUpdate] Estados atualizados:', {
            price,
            priceInReais: price / 100,
            type,
            breakdown,
            reqPayment
          });
        } else {
          console.log('[BookingUpdate] Resposta vazia');
        }
      } catch (error) {
        console.error('[BookingUpdate] Erro ao calcular preço:', error);
      }
    }

    if (environmentId && people > 0) {
      calculatePrice();
    } else {
      console.log('[BookingUpdate] Limpando estados de preço');
      setCalculatedPrice(null);
      setPriceType(null);
      setRequiresPayment(false);
    }
  }, [environmentId, people]);

  async function init() {
    setLoading(true);
    await getConfig();
    await getBookingConfig();
    setLoading(false);
  }

  async function getBooking(bookingConfigItem: BookingConfigType) {
    await api
      .post(
        "/api/queueReservation/booking/filter",
        {
          id,
          bookingConfigId: bookingConfigItem.id,
        },
        {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
          },
        }
      )
      .then((responseBooking) => {
        const bookingItem = responseBooking.data;
        if (bookingItem && bookingItem.data && bookingItem.data[0]) {
          const booking: BookingType = bookingItem.data[0];
          setDateBooking(booking.date);
          changeDate(
            booking.date,
            parseInt(booking.bookingHourId),
            bookingConfigItem
          );
          setEnvironmentId(parseInt(booking.bookingEnvironmentId));
          setPeople(booking.people);
          setName(booking.name);
          if (booking.phone.length >= 11) {
            setMaskTelephone("(99) 99999-9999");
          }
          setPhone(booking.phone);
          setEmail(booking.email);
          if (booking.accessibilityNeeds) {
            setAccessibilityNeeds(booking.accessibilityNeeds);
          }
        } else {
          Swal.fire({
            icon: "error",
            title: "Erro ao encontrar reserva!",
            text: "Não foi possível encontrar reserva.",
          }).then(() => navigate(`/${pluginspace_id}/booking`));
        }
      })
      .catch((err) => {
        console.log(err);
        let title = "Erro ao encontrar reserva!";
        let text = "Não foi possível encontrar reserva.";
        if (
          err &&
          err.response &&
          err.response.data &&
          err.response.data.error
        ) {
          title = err.response.data.error;
        }
        if (
          err &&
          err.response &&
          err.response.data &&
          err.response.data.message
        ) {
          text = err.response.data.message;
        }
        Swal.fire({
          icon: "error",
          title,
          text,
        }).then(() => navigate(`/${pluginspace_id}/booking`));
      });
  }

  async function getBookingConfig() {
    await api
      .get("/api/queueReservation/bookingConfig", {
        headers: {
          Authorization: `Bearer ${await getToken()}`,
        },
      })
      .then(async (response) => {
        const bookingResponse = response.data;
        if (
          bookingResponse &&
          bookingResponse.data &&
          bookingResponse.data[0]
        ) {
          const bookingConfigItem: BookingConfigType = bookingResponse.data[0];
          if (bookingConfigItem && bookingConfigItem.able) {
            setBookingConfig(bookingConfigItem);
            await getBooking(bookingConfigItem);
          } else {
            Swal.fire({
              icon: "error",
              title: "Reserva não está habilitada!",
              text: "Loja não está com o esquema de reservas habilitada.",
            }).then(() => navigate(`/${pluginspace_id}`));
          }
        } else {
          Swal.fire({
            icon: "error",
            title: "Reserva não configurada!",
            text: "Loja não está com o esquema de reserva configurada.",
          }).then(() => navigate(`/${pluginspace_id}`));
        }
      })
      .catch((err) => {
        console.log(err);
        Swal.fire({
          icon: "error",
          title: "Reserva não configurada!",
          text: "Loja não está com o esquema de reservas configurada.",
        }).then(() => navigate(`/${pluginspace_id}`));
      });
  }

  function filterHour(hour: string, date: string) {
    const now = new Date().getTime();
    const timeToCompare = new Date(`${date} ${hour}`).getTime();
    if (now > timeToCompare) return false;
    return true;
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    changeBooking();
  };

  async function changeBooking() {
    // Validar todos os campos com Zod
    try {
      bookingSchema.parse({
        name,
        email,
        people,
        dateBooking,
        hourId,
        environmentId
      });
    } catch (error) {
      if (error instanceof z.ZodError) {
        const zodError = error as z.ZodError;
        // Marcar todos os campos como tocados
        const allTouched = {
          name: true,
          email: true,
          people: true,
          dateBooking: true,
          hourId: true,
          environmentId: true
        };
        setTouchedFields(allTouched);
        
        // Mapear erros
        const errorMap: Partial<Record<keyof BookingFormData, string>> = {};
        zodError.issues.forEach((issue) => {
          if (issue.path[0]) {
            errorMap[issue.path[0] as keyof BookingFormData] = issue.message;
          }
        });
        setErrors(errorMap);
        
        Swal.fire({
          icon: "warning",
          title: "Atenção!",
          text: "Por favor, corrija os erros no formulário antes de continuar.",
        });
        return;
      }
    }

    if (bookingConfig?.maxPeople && people > bookingConfig.maxPeople) {
      Swal.fire({
        icon: "warning",
        title: "Atenção!",
        html: `Há mais pessoas do que o permitido na reserva<br>(Máximo ${bookingConfig.maxPeople})`,
      });
      return;
    }

    if (!bookingConfig?.id) {
      Swal.fire({
        icon: "error",
        title: "Erro!",
        text: `Erro ao encontrar configuração de reserva`,
      });
      return;
    }
    const hour = hours.find((h) => h.value === hourId);
    setLoading(true);
    const tk = await getToken();
    const pg = await getThisPluginspace(tk ? tk : "");
    if (pg && pg.gmt && hour?.select) {
      setLoading(true);
      const date = new Date(
        `${new Date().toString().split("GMT")[0]}${pg.gmt}`
      );
      if (
        date.getTime() >
        new Date(`${dateBooking} ${hour.select} GMT${pg.gmt}`).getTime()
      ) {
        Swal.fire({
          icon: "error",
          title: "Erro!",
          text: `Horário de reserva indisponível`,
        });
        setLoading(false);
        return;
      }
    }
    await api
      .put(
        `/api/queueReservation/booking/${id}`,
        {
          date: dateBooking,
          bookingHourId: hourId,
          bookingEnvironmentId: environmentId,
          email,
          bookingConfigId: bookingConfig.id,
          people,
          name,
          phone: telephoneNoMask(phone),
          accessibilityNeeds: accessibilityNeeds
        },
        {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
          },
        }
      )
      .then((response) => {
        setLoading(false);
        const bookingResponse = response.data;
        if (bookingResponse.success) {
          Swal.fire({
            icon: "success",
            title: "Sucesso!",
            text: "Reserva atualizada.",
          }).then(() =>
            navigate(`/${pluginspace_id}/booking/success/${telephoneNoMask(phone)}`)
          );
        } else {
          Swal.fire({
            icon: "error",
            title: "Erro ao atualizar reserva!",
            text: "Não foi possível atualizar reserva.",
          }).then(async () => await init());
        }
      })
      .catch((err) => {
        setLoading(false);
        let title = "Erro ao atualizar reserva!";
        let text = "Não foi possível atualizar reserva.";
        if (
          err &&
          err.response &&
          err.response.data &&
          err.response.data.error
        ) {
          title = err.response.data.error;
        }
        if (
          err &&
          err.response &&
          err.response.data &&
          err.response.data.message
        ) {
          text = err.response.data.message;
        }
        Swal.fire({
          icon: "error",
          title,
          text,
        }).then(async () => await init());
      });
  }

  async function getConfig() {
    setConfig(await getAppConfig());
  }

  function changePhone(newPhone: string) {
    const phoneValidated = newPhone.replaceAll("_", "");
    if (phoneValidated.length >= 14 && phoneValidated[13] !== "_") {
      setMaskTelephone("(99) 99999-9999");
    } else {
      setMaskTelephone("(99) 9999-9999");
    }
  }

  function changeDate(
    data: string,
    hour?: number,
    bookingConfigItem?: BookingConfigType
  ) {
    const config = bookingConfigItem ? bookingConfigItem : bookingConfig;
    if (hour) setHourId(hour);
    else setHourId(0);

    const fullData = new Date(data);
    const week = getWeekDay(fullData.getDay());
    let newHours = [{ value: 0, select: "Selecione um horário" }];

    if (config?.days) {
      const hoursFind = config.days.find((day) => day.day === week);
      if (
        hoursFind &&
        hoursFind.able &&
        hoursFind.hours &&
        hoursFind.hours.length > 0
      ) {
        const hoursNext = hoursFind.hours
          .map((hour) => ({ value: parseInt(hour.id), select: hour.hour }))
          .sort((a, b) =>
            parseInt(a.select.split(":")[0]) > parseInt(b.select.split(":")[0])
              ? 1
              : parseInt(b.select.split(":")[0]) >
                parseInt(a.select.split(":")[0])
              ? -1
              : 0
          )
          .filter((hour) => filterHour(hour.select, data));
        setHours([...newHours, ...hoursNext]);
        setDateBooking(data);
        return;
      }
    }
    Swal.fire({
      icon: "warning",
      title: "Atenção!",
      text: `Não há horários disponíveis para ${translateWeekday(week)}.`,
    });
  }

  return (
    <>
      <BookingHeader />
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        padding: '40px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflowX: 'hidden',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        {loading ? (
          <div style={{
            position: 'fixed',
            top: '80px',
            left: 0,
            right: 0,
            height: 'calc(100vh - 80px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(248, 249, 250, 0.95)',
            zIndex: 9999
          }}>
            <Loading type="spinningBubbles" color="#6366f1" width={64} height={64} />
          </div>
        ) : (
          <div style={{
            maxWidth: '800px',
            margin: '0 auto',
            width: '100%'
          }}>
          {bookingConfig && (
            <div className="body-booking">
              {/* {bookingConfig.images && bookingConfig.images[0] && (
                <div className="booking-image">
                  <img
                    src={bookingConfig.images[0].url}
                    alt="Imagem Reserva 1"
                  />
                </div>
              )} */}
              <div className="booking-form booking-form-container">
                {/* Page Header */}
                <div style={{
                  textAlign: "center",
                  marginBottom: "40px"
                }}>
                  <div style={{
                    display: "flex",
                    justifyContent: "center",
                    marginBottom: "20px"
                  }}>
                    <div style={{ position: "relative" }}>
                      <div style={{
                        position: "absolute",
                        inset: 0,
                        background: "linear-gradient(135deg, #4338ca 0%, #764ba2 100%)",
                        borderRadius: "24px",
                        filter: "blur(20px)",
                        opacity: 0.4
                      }} />
                      <div style={{
                        position: "relative",
                        padding: "16px",
                        borderRadius: "24px",
                        background: "linear-gradient(135deg, #4338ca 0%, #764ba2 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}>
                        <Sparkles size={32} color="white" style={{ filter: "brightness(1.2)" }} />
                      </div>
                    </div>
                  </div>
                  <div>
                    <h1 style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: "clamp(2rem, 5vw, 3rem)",
                      fontWeight: "800",
                      color: "#2d3436",
                      marginBottom: "12px",
                      letterSpacing: "-0.5px"
                    }}>
                      Dados da <span style={{
                        background: "linear-gradient(135deg, #4338ca 0%, #764ba2 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text"
                      }}>Reserva</span>
                    </h1>
                    <p style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: "500",
                      fontSize: "16px",
                      color: "#636e72",
                      lineHeight: "1.6",
                      maxWidth: "500px",
                      margin: "0 auto"
                    }}>
                      Atualize os dados da sua reserva conforme necessário.
                    </p>
                  </div>
                </div>

                {/* Form Container */}
                <form onSubmit={handleSubmit} style={{
                  width: "100%",
                  margin: 0,
                  padding: 0
                }}>
                
                {/* Card Unificado - Todos os dados da reserva */}
                <div 
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-3px)";
                    e.currentTarget.style.boxShadow = "0 12px 32px rgba(102, 126, 234, 0.22), 0 8px 16px rgba(102, 126, 234, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.6)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 8px 24px rgba(102, 126, 234, 0.15), 0 4px 12px rgba(102, 126, 234, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5)";
                  }}
                  style={{
                    background: "linear-gradient(135deg, rgba(99, 102, 241, 0.12) 0%, rgba(79, 70, 229, 0.08) 50%, rgba(67, 56, 202, 0.1) 100%)",
                    backdropFilter: "blur(20px)",
                    WebkitBackdropFilter: "blur(20px)",
                    borderRadius: "20px",
                    padding: "32px",
                    marginBottom: "16px",
                    boxShadow: "0 8px 24px rgba(102, 126, 234, 0.15), 0 4px 12px rgba(102, 126, 234, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5)",
                    border: "1px solid rgba(102, 126, 234, 0.25)",
                    position: "relative" as const,
                    overflow: "hidden",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
                  }}
                >
                  <div style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: "linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%)",
                    pointerEvents: "none"
                  }}></div>
                  <div style={{ position: "relative", zIndex: 1 }}>
                    <h3 style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: "700",
                      fontSize: "22px",
                      marginBottom: "24px",
                      color: "#4338ca",
                      textAlign: "center",
                      letterSpacing: "0.5px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px"
                    }}>
                      <Sparkles size={20} color="#4338ca" />
                      Dados da Reserva
                    </h3>
                  
                    {/* Data e Horário */}
                    <div style={{ marginBottom: "clamp(16px, 4vw, 24px)" }}>
                      <div className="responsive-row">
                        <div className="responsive-col">
                          <label style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontWeight: "600",
                            fontSize: "12px",
                            color: "#3730a3",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px"
                          }}>
                            <Calendar size={14} style={{ color: "#3730a3" }} />
                            Data
                          </label>
                          <input
                            type="date"
                            value={dateBooking}
                            onChange={(event) => {
                              changeDate(event.target.value);
                              if (touchedFields.dateBooking) {
                                validateField('dateBooking', event.target.value);
                              }
                            }}
                            onBlur={(event) => handleBlur('dateBooking', event.target.value)}
                            min={new Date().toISOString().split("T")[0]}
                            className="field-input"
                          />
                          {touchedFields.dateBooking && errors.dateBooking && (
                            <div style={{
                              color: '#f44336',
                              fontSize: '12px',
                              marginTop: '6px',
                              fontFamily: "'Poppins', sans-serif",
                              fontWeight: '500',
                              animation: 'slideDown 0.3s ease-out',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <span>⚠️</span>
                              {errors.dateBooking}
                            </div>
                          )}
                        </div>
                        <div className="responsive-col">
                          <label style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontWeight: "600",
                            fontSize: "12px",
                            color: "#3730a3",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px"
                          }}>
                            <Clock size={14} style={{ color: "#3730a3" }} />
                            Horário
                          </label>
                          <select
                            value={hourId}
                            onChange={(event) => {
                              const value = parseInt(event.target.value);
                              setHourId(value);
                              if (touchedFields.hourId) {
                                validateField('hourId', value);
                              }
                            }}
                            onBlur={(event) => handleBlur('hourId', parseInt(event.target.value))}
                            className="field-input"
                          >
                            {hours.map((hour) => (
                              <option key={hour.value} value={hour.value}>{hour.select}</option>
                            ))}
                          </select>
                          {touchedFields.hourId && errors.hourId && (
                            <div style={{
                              color: '#f44336',
                              fontSize: '12px',
                              marginTop: '6px',
                              fontFamily: "'Poppins', sans-serif",
                              fontWeight: '500',
                              animation: 'slideDown 0.3s ease-out',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <span>⚠️</span>
                              {errors.hourId}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Divisor visual */}
                  {/*   <div style={{
                      height: "1px",
                      background: "linear-gradient(90deg, transparent 0%, rgba(102, 126, 234, 0.3) 50%, transparent 100%)",
                      margin: "24px 0"
                    }} /> */}
                    
                    {/* Ambiente e Pessoas */}
                    <div style={{ marginBottom: "clamp(16px, 4vw, 24px)" }}>
                      <div className="responsive-row">
                        <div className="responsive-col">
                          <label style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontWeight: "600",
                            fontSize: "12px",
                            color: "#3730a3",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px"
                          }}>
                            <MapPin size={14} style={{ color: "#3730a3" }} />
                            Ambiente
                          </label>
                          {bookingConfig.environment &&
                          bookingConfig.environment.length > 0 ? (
                            <select
                              value={environmentId}
                              onChange={(event) => {
                                const value = parseInt(event.target.value);
                                setEnvironmentId(value);
                                if (touchedFields.environmentId) {
                                  validateField('environmentId', value);
                                }
                              }}
                              onBlur={(event) => handleBlur('environmentId', parseInt(event.target.value))}
                              className="field-input"
                            >
                              <option value={0}>Escolha um ambiente</option>
                              {bookingConfig.environment.map((environment) => (
                                <option key={environment.id} value={environment.id}>
                                  {environment.name}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <select
                              value={environmentId}
                              onChange={(event) => {
                                const value = parseInt(event.target.value);
                                setEnvironmentId(value);
                                if (touchedFields.environmentId) {
                                  validateField('environmentId', value);
                                }
                              }}
                              onBlur={(event) => handleBlur('environmentId', parseInt(event.target.value))}
                              className="field-input"
                            >
                              <option value={0}>Escolha um ambiente</option>
                            </select>
                          )}
                          {touchedFields.environmentId && errors.environmentId && (
                            <div style={{
                              color: '#f44336',
                              fontSize: '12px',
                              marginTop: '6px',
                              fontFamily: "'Poppins', sans-serif",
                              fontWeight: '500',
                              animation: 'slideDown 0.3s ease-out',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <span>⚠️</span>
                              {errors.environmentId}
                            </div>
                          )}
                        </div>
                        <div className="responsive-col">
                          <label style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontWeight: "600",
                            fontSize: "12px",
                            color: "#3730a3",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px"
                          }}>
                            <Users size={14} style={{ color: "#3730a3" }} />
                            Pessoas
                          </label>
                          <input
                            type="number"
                            value={people === 0 ? "" : people}
                            placeholder="0"
                            onChange={(event) => {
                              const value = parseInt(event.target.value) || 0;
                              setPeople(value);
                              if (touchedFields.people) {
                                validateField('people', value);
                              }
                            }}
                            onBlur={(event) => handleBlur('people', parseInt(event.target.value) || 0)}
                            max={bookingConfig.maxPeople}
                            className="field-input"
                          />
                          {touchedFields.people && errors.people && (
                            <div style={{
                              color: '#f44336',
                              fontSize: '12px',
                              marginTop: '6px',
                              fontFamily: "'Poppins', sans-serif",
                              fontWeight: '500',
                              animation: 'slideDown 0.3s ease-out',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <span>⚠️</span>
                              {errors.people}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Divisor visual */}
                   {/*  <div style={{
                      height: "1px",
                      background: "linear-gradient(90deg, transparent 0%, rgba(102, 126, 234, 0.3) 50%, transparent 100%)",
                      margin: "24px 0"
                    }} /> */}
                    
                    {/* Dados Pessoais */}
                    <div>
                      <div className="responsive-col" style={{ marginBottom: "clamp(12px, 3vw, 16px)" }}>
                        <label style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontWeight: "600",
                          fontSize: "12px",
                          color: "#3730a3",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px"
                        }}>
                          <User size={14} style={{ color: "#3730a3" }} />
                          Nome Completo
                        </label>
                        <input
                          type="text"
                          value={name}
                          onChange={(event) => {
                            const isValidName = /^[a-zA-ZÀ-ÿ\s]*$/.test(
                              event.target.value
                            );
                            if (isValidName) {
                              setName(event.target.value);
                              if (touchedFields.name) {
                                validateField('name', event.target.value);
                              }
                            }
                          }}
                          onBlur={(event) => handleBlur('name', event.target.value)}
                          className="field-input"
                        />
                        {touchedFields.name && errors.name && (
                          <div style={{
                            color: '#f44336',
                            fontSize: '12px',
                            marginTop: '6px',
                            fontFamily: "'Poppins', sans-serif",
                            fontWeight: '500',
                            animation: 'slideDown 0.3s ease-out',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <span>⚠️</span>
                            {errors.name}
                          </div>
                        )}
                      </div>
                      <div className="responsive-col" style={{ marginBottom: "clamp(12px, 3vw, 16px)" }}>
                        <label style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontWeight: "600",
                          fontSize: "12px",
                          color: "#3730a3",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px"
                        }}>
                          <Phone size={14} style={{ color: "#3730a3" }} />
                          Celular
                        </label>
                        <InputMask
                          mask={maskTelephone}
                          type="text"
                          value={phone}
                          disabled
                          onBeforeInputCapture={(event) =>
                            changePhone(event.currentTarget.value)
                          }
                          onChange={(event) => setPhone(event.target.value)}
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontWeight: "500",
                            width: "100%",
                            padding: "12px",
                            borderRadius: "8px",
                            border: "1px solid rgba(102, 126, 234, 0.3)",
                            fontSize: "14px",
                            boxSizing: "border-box",
                            height: "44px",
                            background: "rgba(255, 255, 255, 0.5)",
                            cursor: "not-allowed",
                            opacity: "0.7"
                          }}
                        />
                      </div>
                      <div className="responsive-col" style={{ marginBottom: "clamp(12px, 3vw, 16px)" }}>
                        <label style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontWeight: "600",
                          fontSize: "12px",
                          color: "#3730a3",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px"
                        }}>
                          <Mail size={14} style={{ color: "#3730a3" }} />
                          E-mail
                        </label>
                        <input
                          type="text"
                          value={email}
                          onChange={(event) => {
                            setEmail(event.target.value);
                            if (touchedFields.email) {
                              validateField('email', event.target.value);
                            }
                          }}
                          onBlur={(event) => handleBlur('email', event.target.value)}
                          className="field-input"
                        />
                        {touchedFields.email && errors.email && (
                          <div style={{
                            color: '#f44336',
                            fontSize: '12px',
                            marginTop: '6px',
                            fontFamily: "'Poppins', sans-serif",
                            fontWeight: '500',
                            animation: 'slideDown 0.3s ease-out',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <span>⚠️</span>
                            {errors.email}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Necessidades de Acessibilidade - Card com glass morphism */}
                <div 
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-3px)";
                    e.currentTarget.style.boxShadow = "0 12px 32px rgba(102, 126, 234, 0.22), 0 8px 16px rgba(102, 126, 234, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.6)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 8px 24px rgba(102, 126, 234, 0.15), 0 4px 12px rgba(102, 126, 234, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5)";
                  }}
                  style={{
                    background: "linear-gradient(135deg, rgba(99, 102, 241, 0.12) 0%, rgba(79, 70, 229, 0.08) 50%, rgba(67, 56, 202, 0.1) 100%)",
                    backdropFilter: "blur(20px)",
                    WebkitBackdropFilter: "blur(20px)",
                    borderRadius: "20px",
                    padding: "24px",
                    marginBottom: "32px",
                    boxShadow: "0 8px 24px rgba(102, 126, 234, 0.15), 0 4px 12px rgba(102, 126, 234, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5)",
                    border: "1px solid rgba(102, 126, 234, 0.25)",
                    position: "relative" as const,
                    overflow: "hidden",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
                  }}
                >
                  <div style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: "linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%)",
                    pointerEvents: "none"
                  }}></div>
                  <div style={{ position: "relative", zIndex: 1 }}>
                    <h3 style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: "700",
                      fontSize: "14px",
                      marginBottom: "8px",
                      color: "#4338ca",
                      textTransform: "uppercase",
                      letterSpacing: "1.2px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px"
                    }}>
                      <Accessibility size={16} color="#4338ca" />
                      Necessidades de Acessibilidade
                    </h3>
                    <p style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: "400",
                      fontSize: "13px",
                      color: "#6a5acd",
                      marginBottom: "16px"
                    }}>
                      Selecione se sua reserva necessita de algum apoio especial
                    </p>
                    
                    {/* Opção: Apoio ao Idoso */}
                    <div 
                      onClick={() => setAccessibilityNeeds(accessibilityNeeds === 'Apoio ao Idoso' ? null : 'Apoio ao Idoso')}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        padding: "12px",
                        marginBottom: "10px",
                        borderRadius: "12px",
                        border: `2px solid ${accessibilityNeeds === 'Apoio ao Idoso' ? '#4338ca' : 'rgba(102, 126, 234, 0.2)'}`,
                        background: accessibilityNeeds === 'Apoio ao Idoso' ? 'rgba(102, 126, 234, 0.08)' : 'rgba(255, 255, 255, 0.5)',
                        cursor: "pointer",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        transform: accessibilityNeeds === 'Apoio ao Idoso' ? 'scale(1.02)' : 'scale(1)'
                      }}
                    >
                      <div style={{
                        width: "16px",
                        height: "16px",
                        borderRadius: "50%",
                        border: `2px solid ${accessibilityNeeds === 'Apoio ao Idoso' ? '#4338ca' : '#9aa7ea'}`,
                        background: accessibilityNeeds === 'Apoio ao Idoso' ? '#4338ca' : 'transparent',
                        marginRight: "10px",
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.3s"
                      }}>
                        {accessibilityNeeds === 'Apoio ao Idoso' && (
                          <div style={{
                            width: "6px",
                            height: "6px",
                            borderRadius: "50%",
                            background: "#fff"
                          }}></div>
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <h4 style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontWeight: "600",
                          fontSize: "13px",
                          color: "#5e54c5",
                          marginBottom: "2px"
                        }}>Apoio ao Idoso</h4>
                        <p style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontWeight: "400",
                          fontSize: "11px",
                          color: "#7a6bd1",
                          margin: 0
                        }}>Assistência especial para pessoas idosas</p>
                      </div>
                    </div>

                    {/* Opção: Cadeirante */}
                    <div 
                      onClick={() => setAccessibilityNeeds(accessibilityNeeds === 'Cadeirante' ? null : 'Cadeirante')}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        padding: "12px",
                        marginBottom: "10px",
                        borderRadius: "12px",
                        border: `2px solid ${accessibilityNeeds === 'Cadeirante' ? '#4338ca' : 'rgba(102, 126, 234, 0.2)'}`,
                        background: accessibilityNeeds === 'Cadeirante' ? 'rgba(102, 126, 234, 0.08)' : 'rgba(255, 255, 255, 0.5)',
                        cursor: "pointer",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        transform: accessibilityNeeds === 'Cadeirante' ? 'scale(1.02)' : 'scale(1)'
                      }}
                    >
                      <div style={{
                        width: "16px",
                        height: "16px",
                        borderRadius: "50%",
                        border: `2px solid ${accessibilityNeeds === 'Cadeirante' ? '#4338ca' : '#9aa7ea'}`,
                        background: accessibilityNeeds === 'Cadeirante' ? '#4338ca' : 'transparent',
                        marginRight: "10px",
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.3s"
                      }}>
                        {accessibilityNeeds === 'Cadeirante' && (
                          <div style={{
                            width: "6px",
                            height: "6px",
                            borderRadius: "50%",
                            background: "#fff"
                          }}></div>
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <h4 style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontWeight: "600",
                          fontSize: "13px",
                          color: "#5e54c5",
                          marginBottom: "2px"
                        }}>Cadeirante</h4>
                        <p style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontWeight: "400",
                          fontSize: "11px",
                          color: "#7a6bd1",
                          margin: 0
                        }}>Acesso e espaço para cadeira de rodas</p>
                      </div>
                    </div>

                    {/* Opção: Deficiente Físico */}
                    <div 
                      onClick={() => setAccessibilityNeeds(accessibilityNeeds === 'Deficiente Físico' ? null : 'Deficiente Físico')}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        padding: "12px",
                        borderRadius: "12px",
                        border: `2px solid ${accessibilityNeeds === 'Deficiente Físico' ? '#4338ca' : 'rgba(102, 126, 234, 0.2)'}`,
                        background: accessibilityNeeds === 'Deficiente Físico' ? 'rgba(102, 126, 234, 0.08)' : 'rgba(255, 255, 255, 0.5)',
                        cursor: "pointer",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        transform: accessibilityNeeds === 'Deficiente Físico' ? 'scale(1.02)' : 'scale(1)'
                      }}
                    >
                      <div style={{
                        width: "16px",
                        height: "16px",
                        borderRadius: "50%",
                        border: `2px solid ${accessibilityNeeds === 'Deficiente Físico' ? '#4338ca' : '#9aa7ea'}`,
                        background: accessibilityNeeds === 'Deficiente Físico' ? '#4338ca' : 'transparent',
                        marginRight: "10px",
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.3s"
                      }}>
                        {accessibilityNeeds === 'Deficiente Físico' && (
                          <div style={{
                            width: "6px",
                            height: "6px",
                            borderRadius: "50%",
                            background: "#fff"
                          }}></div>
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <h4 style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontWeight: "600",
                          fontSize: "13px",
                          color: "#5e54c5",
                          marginBottom: "2px"
                        }}>PcD</h4>
                        <p style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontWeight: "400",
                          fontSize: "11px",
                          color: "#7a6bd1",
                          margin: 0
                        }}>Suporte para mobilidade reduzida</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Preview de Preço Integrado - Card separado com glass morphism */}
                {calculatedPrice !== null && (
                  <div 
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-3px)";
                      e.currentTarget.style.boxShadow = "0 12px 32px rgba(16, 185, 129, 0.22), 0 8px 16px rgba(16, 185, 129, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.6)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 8px 24px rgba(16, 185, 129, 0.15), 0 4px 12px rgba(16, 185, 129, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5)";
                    }}
                    style={{
                      background: "linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(5, 150, 105, 0.08) 50%, rgba(4, 120, 87, 0.1) 100%)",
                      backdropFilter: "blur(20px)",
                      WebkitBackdropFilter: "blur(20px)",
                      borderRadius: "20px",
                      padding: "24px",
                      marginBottom: "32px",
                      boxShadow: "0 8px 24px rgba(16, 185, 129, 0.15), 0 4px 12px rgba(16, 185, 129, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5)",
                      border: "1px solid rgba(16, 185, 129, 0.25)",
                      position: "relative" as const,
                      overflow: "hidden",
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
                    }}
                  >
                    <div style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: "linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%)",
                      pointerEvents: "none"
                    }}></div>
                    <div style={{ position: "relative", zIndex: 1 }}>
                      <h3 style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: "700",
                        fontSize: "14px",
                        marginBottom: "8px",
                        color: "#10b981",
                        textTransform: "uppercase",
                        letterSpacing: "1.2px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}>
                        <DollarSign size={16} color="#10b981" />
                        Preço da Reserva
                      </h3>
                      <p style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: "400",
                        fontSize: "13px",
                        color: "#059669",
                        marginBottom: "16px"
                      }}>
                        Confira o valor calculado para sua reserva
                      </p>
                      
                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "16px",
                        marginBottom: "12px"
                      }}>
                        <div style={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column" as const,
                          alignItems: "center",
                          padding: "16px",
                          background: "rgba(255, 255, 255, 0.3)",
                          borderRadius: "12px",
                          border: "1px solid rgba(16, 185, 129, 0.2)"
                        }}>
                          <span style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontWeight: "500",
                            fontSize: "11px",
                            color: "#047857",
                            textTransform: "uppercase",
                            letterSpacing: "1px",
                            marginBottom: "4px"
                          }}>
                            Total
                          </span>
                          <span style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontWeight: "800",
                            fontSize: "24px",
                            color: "#10b981",
                            lineHeight: 1
                          }}>
                            {formatCurrency(priceInReais)}
                          </span>
                        </div>
                        
                        {priceType && (
                          <div style={{
                            display: "flex",
                            flexDirection: "column" as const,
                            alignItems: "flex-end",
                            gap: "4px"
                          }}>
                            <span style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontWeight: "600",
                              fontSize: "12px",
                              color: "#047857",
                              background: "rgba(16, 185, 129, 0.1)",
                              padding: "4px 8px",
                              borderRadius: "6px",
                              border: "1px solid rgba(16, 185, 129, 0.2)"
                            }}>
                              {priceType}
                            </span>
                            {requiresPayment && (
                              <span style={{
                                fontFamily: "'Poppins', sans-serif",
                                fontWeight: "500",
                                fontSize: "10px",
                                color: "#f59e0b",
                                background: "rgba(245, 158, 11, 0.1)",
                                padding: "2px 6px",
                                borderRadius: "4px",
                                border: "1px solid rgba(245, 158, 11, 0.3)"
                              }}>
                                Requer pagamento
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {priceBreakdown && (
                        <div style={{
                          padding: "12px",
                          background: "rgba(255, 255, 255, 0.2)",
                          borderRadius: "8px",
                          border: "1px solid rgba(16, 185, 129, 0.15)"
                        }}>
                          <span style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontWeight: "500",
                            fontSize: "10px",
                            color: "#047857",
                            textTransform: "uppercase",
                            letterSpacing: "1px"
                          }}>
                            Detalhamento do preço disponível
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Action Button */}
                <button
                  onClick={() => changeBooking()}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 8px 24px rgba(102, 126, 234, 0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 4px 16px rgba(102, 126, 234, 0.3)";
                  }}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "12px",
                    padding: "16px",
                    marginTop: "24px",
                    border: "none",
                    borderRadius: "16px",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: "600",
                    fontSize: "16px",
                    cursor: "pointer",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    boxShadow: "0 4px 16px rgba(102, 126, 234, 0.3)"
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Atualizar Reserva
                </button>
                  </form>
              </div>
            </div>
          )}
        </div>
      )}
      </div>
    </>
  );
}





